import { Button, Dialog, FormField, Input, Stack, toast } from "@nudgebox/ui";
import { PlusIcon } from "@nudgebox/ui/icons";
import { slugify } from "@nudgebox/utils";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { errorMessage, fieldErrors } from "@/lib/errors.ts";
import { useCreateApplication } from "./mutations.ts";

export interface CreateApplicationDialogProps {
  orgSlug: string;
  primary?: boolean;
  /** Control the dialog from elsewhere (e.g. the command palette); the trigger button is then omitted. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CreateApplicationDialog({ orgSlug, primary = true, ...controlled }: CreateApplicationDialogProps) {
  const navigate = useNavigate();
  const create = useCreateApplication(orgSlug);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlled.open !== undefined;
  const open = controlled.open ?? uncontrolledOpen;
  const setOpen = (next: boolean) => (isControlled ? controlled.onOpenChange?.(next) : setUncontrolledOpen(next));
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState("");

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const errors = fieldErrors(create.error);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const app = await create.mutateAsync({ name, slug: effectiveSlug || undefined, websiteUrl });
    toast.success("Application created", `Share ${app.subscribeUrl} to collect subscribers.`);
    setOpen(false);
    await navigate({ to: "/$orgSlug/apps/$appSlug", params: { orgSlug, appSlug: app.slug } });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) create.reset();
      }}
      trigger={
        isControlled ? undefined : (
          <Button variant={primary ? "primary" : "secondary"} icon={<PlusIcon size={14} />}>
            New application
          </Button>
        )
      }
      title="New application"
      description="A website or product people can subscribe to."
      footer={
        <>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            form="create-application"
            loading={create.isPending}
            disabled={!name.trim()}
          >
            Create application
          </Button>
        </>
      }
    >
      <form id="create-application" onSubmit={(event) => void onSubmit(event).catch(() => {})}>
        <Stack gap="lg">
          <FormField label="Name" error={errors.name}>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Store" />
          </FormField>
          <FormField
            label="Slug"
            description="Used in your public subscription link. Lowercase letters, numbers and hyphens."
            error={errors.slug ?? (create.error && !Object.keys(errors).length ? errorMessage(create.error) : null)}
          >
            <Input
              mono
              value={effectiveSlug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              placeholder="acme-store"
            />
          </FormField>
          <FormField label="Website" aside="Optional" error={errors.websiteUrl}>
            <Input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://acme.com"
            />
          </FormField>
        </Stack>
      </form>
    </Dialog>
  );
}
